import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Patient } from '../../types/types';
import { useAccessToken } from '@features/user';
import APIService from '../../services/APIService';
import { NextAppointmentCard } from '@features/appointments';
import { CalendarMonth } from '@mui/icons-material';
import BaseBlock from './Blocks/BaseBlock';
import { AddAppointmentModal } from '@features/appointments';
import { useGetNextAppointmentQuery } from '@features/appointments';

interface PatientNextAppointmentProps {
  patientId: string;
}

/**
 * Component that fetches and displays the next appointment for a patient
 */
const PatientNextAppointment: React.FC<PatientNextAppointmentProps> = ({ patientId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const { getAccessToken } = useAccessToken();

  const { data: appointmentsData, isLoading } = useGetNextAppointmentQuery({
    patientId,
    num: 1,
  });

  const nextAppointment = appointmentsData?.items?.[0] || null;

  useEffect(() => {
    const fetchPatientData = async () => {
      if (!patientId) return;

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.error('Failed to get access token');
          return;
        }

        const response = await APIService.makeAPIGetRequest({
          requestString: `/patient/get?patient_id=${patientId}`,
          accessToken,
        });

        if (response.ok) {
          setPatient(response.value.patient);
        }
      } catch (error) {
        console.error('Error fetching patient data:', error);
      }
    };

    fetchPatientData();
  }, [patientId, getAccessToken]);

  const handleScheduleAppointment = () => {
    setModalOpen(true);
  };

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  if (isLoading) {
    return (
      <Box>
        <NextAppointmentCard
          isLoading={true}
          showPatientName={false}
          title="UPCOMING APPOINTMENT"
        />
      </Box>
    );
  }

  if (!nextAppointment) {
    return (
      <>
        <BaseBlock>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarMonth sx={{ color: 'text.secondary' }} />
              <Typography variant="body1" color="text.secondary">
                No upcoming appointment
              </Typography>
            </Box>
            <Button variant="contained" color="primary" onClick={handleScheduleAppointment}>
              Schedule Appointment
            </Button>
          </Box>
        </BaseBlock>

        <AddAppointmentModal open={modalOpen} onClose={handleModalClose} initialPatient={patient} />
      </>
    );
  }

  return (
    <>
      <Box>
        <NextAppointmentCard
          appointment={nextAppointment}
          showPatientName={false}
          title="UPCOMING APPOINTMENT"
        />
      </Box>

      <AddAppointmentModal open={modalOpen} onClose={handleModalClose} initialPatient={patient} />
    </>
  );
};

export default PatientNextAppointment;
